import { Database, RefreshCw, MessageSquare, CheckCircle2 } from "lucide-react";

/**
 * Premium coded "database reactivation" mockup: a dark CRM panel showing dormant
 * leads gone quiet, a reactivation campaign firing at $0 new ad spend, and one
 * past lead waking up and re-booking. Hero visual for the /platform/database-reactivation
 * spoke. Illustrative sample data only (like the other capability mockups), no
 * claimed client results.
 */
const dormant = [
  { initials: "RB", name: "Ray B.", quiet: "cold 14 mo" },
  { initials: "SL", name: "Sandra L.", quiet: "cold 9 mo" },
];

export const DatabaseReactivationMockup = () => {
  return (
    <div className="relative w-full max-w-[420px] mx-auto select-none">
      {/* floating badge */}
      <div className="absolute -top-3 right-4 z-10 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-lg shadow-emerald-900/30">
        Reactivation on autopilot
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900/80 shadow-2xl backdrop-blur-sm">
        {/* CRM header */}
        <div className="border-b border-slate-700 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-500/15 text-emerald-400">
                <Database className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-bold text-white">Your CRM database</p>
                <p className="mt-0.5 text-xs text-slate-400">Past leads &amp; customers gone quiet</p>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-slate-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-300">
              2,146 dormant
            </span>
          </div>
        </div>

        {/* campaign firing strip */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-800 bg-emerald-500/5 px-5 py-3">
          <div className="flex items-center gap-2 text-emerald-300">
            <RefreshCw className="h-4 w-4 animate-spin [animation-duration:3s]" />
            <span className="text-xs font-semibold">Reactivation campaign: sending</span>
          </div>
          <span className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
            $0 ad spend
          </span>
        </div>

        {/* dormant contacts + one revived */}
        <div className="space-y-3 px-5 py-4">
          {dormant.map((d) => (
            <div key={d.initials} className="flex items-center gap-3 opacity-55">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-700 text-[11px] font-bold text-slate-300">
                {d.initials}
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-xs font-bold text-slate-200">{d.name}</span>
                <p className="mt-0.5 text-[11px] text-slate-500">Last contact · {d.quiet}</p>
              </div>
              <span className="h-2 w-2 shrink-0 rounded-full bg-slate-600" />
            </div>
          ))}

          {/* revived row */}
          <div className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-[11px] font-bold text-white">
              MT
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-white">Marcus T.</span>
                <MessageSquare className="h-3 w-3 text-emerald-400" />
              </div>
              <p className="mt-0.5 text-[11px] leading-snug text-emerald-200/80">
                "Yeah, still looking. Can I come in Saturday?"
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/20 px-2.5 py-1 text-[10px] font-bold text-emerald-300">
              <CheckCircle2 className="h-3 w-3" />
              Re-booked
            </span>
          </div>
        </div>
      </div>

      <p className="mt-3 px-4 text-center text-xs text-slate-400">
        Leads you already paid to acquire, reworked automatically until they buy or opt out.
      </p>
    </div>
  );
};

export default DatabaseReactivationMockup;
